/**
 * Lot 6.9B.3B — Safe base64 PDF → Blob / object URL helpers for transient preview.
 * Never persists preview bytes; callers must revoke object URLs when done.
 */

export const LETTER_TEMPLATE_PDF_PREVIEW_MIME = 'application/pdf';
const PDF_SIGNATURE = '%PDF-';

/**
 * @param {string} base64
 * @param {string} [contentType]
 * @returns {Blob}
 */
export function base64PdfToBlob(base64, contentType = LETTER_TEMPLATE_PDF_PREVIEW_MIME) {
  const mime = String(contentType ?? LETTER_TEMPLATE_PDF_PREVIEW_MIME).trim().toLowerCase();
  if (mime !== LETTER_TEMPLATE_PDF_PREVIEW_MIME) {
    throw new Error('letter_template_pdf_preview_invalid_mime');
  }

  const normalized = String(base64 ?? '').replace(/\s/g, '');
  if (!normalized) {
    throw new Error('letter_template_pdf_preview_empty');
  }
  let binary;
  try {
    binary = atob(normalized);
  } catch {
    throw new Error('letter_template_pdf_preview_invalid_base64');
  }
  if (!binary.startsWith(PDF_SIGNATURE)) {
    throw new Error('letter_template_pdf_preview_invalid_signature');
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: LETTER_TEMPLATE_PDF_PREVIEW_MIME });
}

/**
 * @param {Blob} blob
 * @returns {string}
 */
export function createPdfObjectUrl(blob) {
  if (!(blob instanceof Blob)) {
    throw new Error('letter_template_pdf_preview_invalid_blob');
  }
  return URL.createObjectURL(blob);
}

/**
 * @param {string | null | undefined} objectUrl
 */
export function revokePdfObjectUrl(objectUrl) {
  if (!objectUrl) return;
  try {
    URL.revokeObjectURL(objectUrl);
  } catch {
    // Ignore revoke failures on already-revoked URLs.
  }
}

/**
 * @param {{ base64: string, contentType?: string }} params
 * @returns {{ blob: Blob, objectUrl: string }}
 */
export function createTransientPdfPreviewFromBase64({ base64, contentType }) {
  const blob = base64PdfToBlob(base64, contentType);
  return { blob, objectUrl: createPdfObjectUrl(blob) };
}

/**
 * Open a blank preview tab synchronously during the user gesture.
 * @returns {Window | null}
 */
export function openLetterTemplatePdfPreviewWindow() {
  const previewWindow = window.open('about:blank', '_blank');
  if (!previewWindow) return null;
  try {
    previewWindow.opener = null;
  } catch {
    // Best effort — some browsers restrict after navigation.
  }
  return previewWindow;
}

/**
 * @param {Window | null | undefined} previewWindow
 * @param {string} objectUrl
 */
export function navigateLetterTemplatePdfPreviewWindow(previewWindow, objectUrl) {
  if (!previewWindow || !objectUrl) return;
  previewWindow.location.href = objectUrl;
}

/**
 * @param {Window | null | undefined} previewWindow
 */
export function closeLetterTemplatePdfPreviewWindow(previewWindow) {
  if (!previewWindow) return;
  try {
    previewWindow.close();
  } catch {
    // Ignore close failures on already-closed windows.
  }
}

/**
 * Orchestrates one preview attempt: sync popup, async callable, navigate or close.
 *
 * @param {{
 *   enabled?: boolean,
 *   inFlightRef: { current: boolean },
 *   request: object,
 *   invokePreview: (request: object) => Promise<{ base64?: string, contentType?: string }>,
 *   createFromBase64?: typeof createTransientPdfPreviewFromBase64,
 *   openPreviewWindow?: typeof openLetterTemplatePdfPreviewWindow,
 *   closePreviewWindow?: typeof closeLetterTemplatePdfPreviewWindow,
 *   navigatePreviewWindow?: typeof navigateLetterTemplatePdfPreviewWindow,
 * }} params
 */
export async function runLetterTemplatePdfPreviewAttempt({
  enabled = true,
  inFlightRef,
  request,
  invokePreview,
  createFromBase64 = createTransientPdfPreviewFromBase64,
  openPreviewWindow = openLetterTemplatePdfPreviewWindow,
  closePreviewWindow = closeLetterTemplatePdfPreviewWindow,
  navigatePreviewWindow = navigateLetterTemplatePdfPreviewWindow,
}) {
  if (!enabled) return { outcome: 'disabled' };
  if (inFlightRef?.current) return { outcome: 'in_flight' };

  const previewWindow = openPreviewWindow();
  if (!previewWindow) return { outcome: 'popup_blocked' };

  inFlightRef.current = true;
  let popupOpenedAt = Date.now();
  try {
    const result = await invokePreview(request);
    const { objectUrl } = createFromBase64({
      base64: result?.base64,
      contentType: result?.contentType,
    });
    navigatePreviewWindow(previewWindow, objectUrl);
    return { outcome: 'opened', objectUrl, previewWindow, popupOpenedAt };
  } catch (error) {
    closePreviewWindow(previewWindow);
    return { outcome: 'error', error, previewWindow, popupOpenedAt };
  } finally {
    inFlightRef.current = false;
  }
}
